/**
 * PuppetList Component (Task 5.2)
 *
 * Displays a scrollable list of puppets with their name, model path, and tool count.
 * Highlights the selected puppet and allows selection for editing.
 *
 * Features:
 * - Display list of puppets with name, model path, tool count
 * - Highlight selected puppet
 * - Scrollable if many puppets
 * - Add puppet button at bottom
 */

import { Plus } from 'lucide-react';
import { motion } from 'framer-motion';
import { usePuppetStore, type Puppet } from '@/stores/puppetStore';
import { PuppetListItem } from './PuppetListItem';

interface PuppetListProps {
  /**
   * Currently selected puppet ID for editing
   */
  selectedPuppetId: string | null;

  /**
   * Callback when a puppet is selected
   */
  onSelectPuppet: (puppetId: string) => void;

  /**
   * Callback when add puppet button is clicked
   */
  onAddPuppet: () => void;

  /**
   * Callback when delete puppet is requested
   */
  onDeletePuppet: (puppet: Puppet) => void;
}

export function PuppetList({
  selectedPuppetId,
  onSelectPuppet,
  onAddPuppet,
  onDeletePuppet,
}: PuppetListProps) {
  const puppets = usePuppetStore((state) => state.puppets);
  const activePuppetId = usePuppetStore((state) => state.activePuppetId);

  const canDeletePuppet = puppets.length > 1;

  if (puppets.length === 0) {
    return (
      <div
        className="flex h-full flex-col items-center justify-center p-4 text-center text-sm"
        style={{ color: 'var(--color-dark-text-muted)' }}
      >
        <p className="mb-4">No puppets configured</p>
        <motion.button
          onClick={onAddPuppet}
          className="flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium"
          style={{
            backgroundColor: 'var(--color-dark-border)',
            color: 'var(--color-dark-text)',
          }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <Plus className="h-4 w-4" />
          Add Puppet
        </motion.button>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      {/* Puppet list - scrollable area */}
      <div
        className="flex-1 space-y-1 overflow-y-auto p-2"
        role="listbox"
        aria-label="Puppet list"
      >
        {puppets.map((puppet) => (
          <PuppetListItem
            key={puppet.id}
            puppet={puppet}
            isSelected={selectedPuppetId === puppet.id}
            isActive={activePuppetId === puppet.id}
            canDelete={canDeletePuppet}
            onSelect={() => onSelectPuppet(puppet.id)}
            onDelete={() => onDeletePuppet(puppet)}
          />
        ))}
      </div>

      {/* Add puppet button - fixed at bottom */}
      <div
        className="border-t p-3"
        style={{ borderColor: 'var(--color-dark-border)' }}
      >
        <motion.button
          onClick={onAddPuppet}
          className="flex w-full items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors"
          style={{
            backgroundColor: 'var(--color-dark-bg)',
            borderWidth: '1px',
            borderStyle: 'dashed',
            borderColor: 'var(--color-dark-border)',
            color: 'var(--color-dark-text-muted)',
          }}
          whileHover={{
            scale: 1.01,
            borderColor: 'var(--color-dark-text-muted)',
            color: 'var(--color-dark-text)',
          }}
          whileTap={{ scale: 0.99 }}
          aria-label="Add puppet"
        >
          <Plus className="h-4 w-4" />
          Add Puppet
        </motion.button>
      </div>
    </div>
  );
}
